/**
 * TCG Mega Menu JavaScript
 * Handles hover/click interactions, accessibility, and animations
 */

class TCGMegaMenu {
  constructor(container) {
    this.menu = container;
    if (!this.menu) return;

    this.overlay = document.querySelector('.tcg-mega-menu__overlay');
    this.menuItems = this.menu.querySelectorAll('.tcg-mega-menu__item.has-dropdown');
    this.allItems = this.menu.querySelectorAll('.tcg-mega-menu__item');
    this.activeItem = null;
    this.hoverTimeout = null;
    this.closeTimeout = null;
    this.isMobile = window.innerWidth < 990;
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupAccessibility();
    this.handleResize();
  }

  bindEvents() {
    // Menu item events
    this.menuItems.forEach(item => {
      const link = item.querySelector('.tcg-mega-menu__link');
      const dropdown = item.querySelector('.tcg-mega-menu__dropdown');

      if (!dropdown) return;

      // Desktop: Hover behavior
      item.addEventListener('mouseenter', () => this.handleMouseEnter(item));
      item.addEventListener('mouseleave', () => this.handleMouseLeave(item));

      // Keyboard accessibility
      link.addEventListener('keydown', (e) => this.handleKeydown(e, item));

      // Click behavior (mobile and touch)
      link.addEventListener('click', (e) => this.handleClick(e, item, link));
    });

    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeAll());
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAll();
        // Return focus to the active menu item
        if (this.activeItem) {
          const link = this.activeItem.querySelector('.tcg-mega-menu__link');
          if (link) link.focus();
        }
      }
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.menu.contains(e.target) && this.activeItem) {
        this.closeAll();
      }
    });

    // Handle window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.handleResize(), 150);
    });
  }

  handleMouseEnter(item) {
    if (this.isMobile || this.isTouchDevice) return;

    clearTimeout(this.hoverTimeout);
    clearTimeout(this.closeTimeout);

    // Small delay before opening to prevent accidental triggers
    this.hoverTimeout = setTimeout(() => {
      this.openDropdown(item);
    }, 50);
  }

  handleMouseLeave(item) {
    if (this.isMobile || this.isTouchDevice) return;

    clearTimeout(this.hoverTimeout);

    // Delay closing to allow moving to dropdown
    this.closeTimeout = setTimeout(() => {
      this.closeDropdown(item);
    }, 150);
  }

  handleClick(e, item, link) {
    const dropdown = item.querySelector('.tcg-mega-menu__dropdown');
    if (!dropdown) return;

    // On touch devices or mobile, toggle dropdown
    if (this.isTouchDevice || this.isMobile) {
      e.preventDefault();

      if (item.classList.contains('is-open')) {
        this.closeDropdown(item);
      } else {
        this.closeAll();
        this.openDropdown(item);
      }
    }
  }

  handleKeydown(e, item) {
    const dropdown = item.querySelector('.tcg-mega-menu__dropdown');
    const link = item.querySelector('.tcg-mega-menu__link');

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (dropdown) {
          e.preventDefault();
          if (item.classList.contains('is-open')) {
            this.closeDropdown(item);
          } else {
            this.closeAll();
            this.openDropdown(item);
            // Focus first link in dropdown
            this.focusFirstDropdownLink(dropdown);
          }
        }
        break;

      case 'ArrowDown':
        if (dropdown && !item.classList.contains('is-open')) {
          e.preventDefault();
          this.openDropdown(item);
          this.focusFirstDropdownLink(dropdown);
        } else if (item.classList.contains('is-open')) {
          e.preventDefault();
          this.focusFirstDropdownLink(dropdown);
        }
        break;

      case 'ArrowUp':
        if (item.classList.contains('is-open')) {
          e.preventDefault();
          this.closeDropdown(item);
          link.focus();
        }
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.focusNextItem(item);
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.focusPreviousItem(item);
        break;

      case 'Tab':
        // Close dropdown when tabbing away
        if (!e.shiftKey && item.classList.contains('is-open')) {
          // Let natural tab flow, dropdown will close
        }
        break;
    }
  }

  openDropdown(item) {
    // Close others first
    this.menuItems.forEach(otherItem => {
      if (otherItem !== item && otherItem.classList.contains('is-open')) {
        this.closeDropdown(otherItem, false);
      }
    });

    item.classList.add('is-open');
    const dropdown = item.querySelector('.tcg-mega-menu__dropdown');
    const link = item.querySelector('.tcg-mega-menu__link');

    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'false');

      // Position full-width dropdown correctly
      if (dropdown.classList.contains('tcg-mega-menu__dropdown--full')) {
        this.positionFullWidthDropdown(dropdown);
      }
    }

    if (link) {
      link.setAttribute('aria-expanded', 'true');
    }

    // Show overlay
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }

    this.activeItem = item;
  }

  closeDropdown(item, hideOverlay = true) {
    item.classList.remove('is-open');
    const dropdown = item.querySelector('.tcg-mega-menu__dropdown');
    const link = item.querySelector('.tcg-mega-menu__link');

    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'true');
    }

    if (link) {
      link.setAttribute('aria-expanded', 'false');
    }

    // Only hide overlay if this was the active item
    if (hideOverlay && this.activeItem === item) {
      this.activeItem = null;
      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
      }
    }
  }

  closeAll() {
    this.menuItems.forEach(item => {
      if (item.classList.contains('is-open')) {
        this.closeDropdown(item, false);
      }
    });

    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }

    this.activeItem = null;
  }

  positionFullWidthDropdown(dropdown) {
    // Get the menu's position relative to viewport
    const menuRect = this.menu.getBoundingClientRect();
    dropdown.style.top = `${menuRect.bottom}px`;
    dropdown.style.left = '0';
  }

  focusFirstDropdownLink(dropdown) {
    requestAnimationFrame(() => {
      const firstLink = dropdown.querySelector('a');
      if (firstLink) {
        firstLink.focus();
      }
    });
  }

  focusNextItem(currentItem) {
    const items = Array.from(this.allItems);
    const currentIndex = items.indexOf(currentItem);
    const nextIndex = (currentIndex + 1) % items.length;
    const nextLink = items[nextIndex].querySelector('.tcg-mega-menu__link');
    if (nextLink) nextLink.focus();
  }

  focusPreviousItem(currentItem) {
    const items = Array.from(this.allItems);
    const currentIndex = items.indexOf(currentItem);
    const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    const prevLink = items[prevIndex].querySelector('.tcg-mega-menu__link');
    if (prevLink) prevLink.focus();
  }

  setupAccessibility() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.tcg-mega-menu__link');
      const dropdown = item.querySelector('.tcg-mega-menu__dropdown');

      if (link && dropdown) {
        // Generate unique ID for dropdown
        const dropdownId = `tcg-mega-dropdown-${Math.random().toString(36).substr(2, 9)}`;
        dropdown.id = dropdownId;

        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-controls', dropdownId);
        dropdown.setAttribute('aria-hidden', 'true');
        dropdown.setAttribute('role', 'menu');
      }
    });

    // Set role on main list
    const list = this.menu.querySelector('.tcg-mega-menu__list');
    if (list) {
      list.setAttribute('role', 'menubar');
    }
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 990;

    // Close all dropdowns on breakpoint change
    if (wasMobile !== this.isMobile) {
      this.closeAll();
    }

    // Update touch device detection
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}

// Initialize all mega menus
function initTCGMegaMenus() {
  const menus = document.querySelectorAll('.tcg-mega-menu');
  menus.forEach(menu => {
    if (!menu.dataset.initialized) {
      new TCGMegaMenu(menu);
      menu.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTCGMegaMenus);
} else {
  initTCGMegaMenus();
}

// Re-initialize if Shopify theme editor updates sections
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initTCGMegaMenus);
  document.addEventListener('shopify:section:reorder', initTCGMegaMenus);
}
